<template>
  <v-navigation-drawer
    :value="isDrawerOpen"
    app
    floating
    width="230"
    mini-variant-width="80"
    :mini-variant="isMini"
    class="app-navigation-menu"
    :right="$vuetify.rtl"
    @input="val => $emit('update:is-drawer-open', val)"
  >
    <div class="vertical-nav-header d-flex items-center ps-6 pe-5 pt-5 pb-2">
      <router-link v-if="!isMini" to="/" class="d-flex align-center text-decoration-none">
        <v-img
          :src="require(`@/assets/images/logo${$vuetify.theme.dark ? '' : '--dark'}.png`)"
          max-height="30px"
          alt="logo"
          contain
          eager
          class="app-logo me-3"
        ></v-img>
      </router-link>

      <v-spacer v-if="!isMini" />

      <v-btn icon small class="nav-mini-toggle" @click="$emit('update:is-mini', !isMini)">
        <v-icon>{{ isMini ? icons.mdiChevronRight : icons.mdiChevronLeft }}</v-icon>
      </v-btn>
    </div>

    <v-list expand shaped class="vertical-nav-menu-items" :class="{ 'pr-0': !isMini }">
      <nav-menu-link title="Dashboard" :to="{ name: 'dashboard' }" :icon="icons.mdiHomeOutline" />

      <nav-menu-group
        class="nav-menu-section-group"
        title="FITNESS"
        :value="navMenuGroups.fitness"
        @input="setNavMenuGroupState('fitness', $event)"
      >
        <nav-menu-link title="Fitness Exercises" :to="{ name: 'fitness-exercises' }" :icon="icons.mdiWeightLifter" />
        <nav-menu-link title="Fitness Routines" :to="{ name: 'fitness-routines' }" :icon="icons.mdiAvTimer" />
        <nav-menu-link title="Fitness Assessments" :to="{ name: 'fitness-assessments' }" :icon="icons.mdiDumbbell" />
      </nav-menu-group>

      <nav-menu-group
        class="nav-menu-section-group"
        title="PRACTICE"
        :value="navMenuGroups.practice"
        @input="setNavMenuGroupState('practice', $event)"
      >
        <nav-menu-link title="Practice Drills" :to="{ name: 'practice-drills' }" :icon="icons.mdiGolf" />
        <nav-menu-link title="Practice Sessions" :to="{ name: 'practice-sessions' }" :icon="icons.mdiFileChartOutline" />
        <nav-menu-link title="Golf Skills Assessments" :to="{ name: 'golf-skills-assessments' }" :icon="icons.mdiGolfTee" />
      </nav-menu-group>

      <nav-menu-group
        v-if="isTheFlowEnabled"
        class="nav-menu-section-group"
        title="THE FLOW"
        :value="navMenuGroups.flow"
        @input="setNavMenuGroupState('flow', $event)"
      >
        <nav-menu-link title="Exercises" :to="{ name: 'flow-exercises' }" :svg-icon="theFlowIcons.exercises" />
        <nav-menu-link title="Sessions" :to="{ name: 'flow-sessions' }" :svg-icon="theFlowIcons.sessions" />
        <nav-menu-link title="Assessments" :to="{ name: 'flow-assessments' }" :svg-icon="theFlowIcons.assessments" />
      </nav-menu-group>
    </v-list>

    <template v-if="hasRole('ADMIN')">
      <v-list expand shaped class="vertical-nav-menu-items" :class="{ 'pr-0': !isMini }">
        <nav-menu-group
          class="nav-menu-section-group"
          title="ORGANIZATION"
          :value="navMenuGroups.organization"
          @input="setNavMenuGroupState('organization', $event)"
        >
          <nav-menu-link title="Academies" :to="{ name: 'academies' }" :icon="icons.mdiOfficeBuilding" />
          <nav-menu-link title="Manage Users" :to="{ name: 'manage-coaches' }" :icon="icons.mdiAccountGroup" />
          <nav-menu-link title="Questionnaires" :to="{ name: 'questionnaires' }" :icon="icons.mdiClipboardTextOutline" />
          <nav-menu-link title="Training Plans" :to="{ name: 'training-plans' }" :icon="icons.mdiAvTimer" />
          <nav-menu-link title="Content Packs" :to="{ name: 'content-packs' }" :icon="icons.mdiFolderMultipleOutline" />
          <nav-menu-link title="Reports" :to="{ name: 'reports' }" :icon="icons.mdiFileChartOutline" />
        </nav-menu-group>
      </v-list>
    </template>

    <template v-if="hasRole('COACH') && userAcademyIds.length > 0">
      <v-list expand shaped class="vertical-nav-menu-items" :class="{ 'pr-0': !isMini }">
        <nav-menu-group
          class="nav-menu-section-group"
          title="ORGANIZATION"
          :value="navMenuGroups.organization"
          @input="setNavMenuGroupState('organization', $event)"
        >
          <template v-if="userAcademyIds.length === 1">
            <nav-menu-link
              title="My Academy"
              :to="{ name: 'academy-details', params: { id: userAcademyIds[0] } }"
              :icon="icons.mdiOfficeBuilding"
            />
          </template>
          <template v-else>
            <nav-menu-link
              title="My Academies"
              :to="{ name: 'my-academies' }"
              :icon="icons.mdiOfficeBuilding"
            />
          </template>
        </nav-menu-group>
      </v-list>
    </template>

    <div v-if="hasRole('ADMIN') && !isMini && !selectedAcademy" class="content-scope-switcher">
      <div class="content-scope-title">
        Content Scope
      </div>
      <div class="content-scope-row">
        <v-icon size="18" class="content-scope-icon">
          {{ isFullContentEnabled ? icons.mdiDatabaseSearch : icons.mdiEarth }}
        </v-icon>
        <div class="content-scope-labels">
          <span class="content-scope-main">{{ isFullContentEnabled ? 'Full Content' : 'Global Content' }}</span>
          <span class="content-scope-sub">
            {{ isFullContentEnabled ? 'Everything' : 'AcademyId = null' }}
          </span>
        </div>
        <v-switch
          v-model="isFullContentEnabled"
          dense
          hide-details
          inset
          class="content-scope-switch"
          @change="onContentScopeChange"
        />
      </div>
    </div>

    <build-info v-if="!isMini" class="nav-build-info" />

    <div v-if="isMini" class="mini-rotated-logo">
      <v-img
        :src="require(`@/assets/images/logo${$vuetify.theme.dark ? '' : '--dark'}.png`)"
        width="165"
        alt="Perform72"
        contain
        eager
      ></v-img>
    </div>
  </v-navigation-drawer>
</template>

<script>
import {
  mdiAvTimer,
  mdiDumbbell,
  mdiGolf,
  mdiGolfTee,
  mdiHomeOutline,
  mdiWeightLifter,
  mdiFileChartOutline,
  mdiOfficeBuilding,
  mdiAccountGroup,
  mdiClipboardTextOutline,
  mdiFolderMultipleOutline,
  mdiChevronLeft,
  mdiChevronRight,
  mdiEarth,
  mdiDatabaseSearch,
} from '@mdi/js'
import useAuth from '@/composables/useAuth'
import useAcademies from '@/composables/api/academies'
import useAcademySelection from '@/composables/useAcademySelection'
import { isTheFlowEnabled } from '@/utils/featureFlags'
import { ref, onMounted, watch } from 'vue'
import axios from '@/utils/axios'
import BuildInfo from '@/components/BuildInfo.vue'
import NavMenuLink from './components/NavMenuLink.vue'
import NavMenuGroup from './components/NavMenuGroup.vue'

export default {
  components: {
    BuildInfo,
    NavMenuGroup,
    NavMenuLink,
  },

  props: {
    isDrawerOpen: {
      type: Boolean,
      default: null,
    },
    isMini: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const theFlowIcons = {
      exercises: {
        paths: [
          'M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z',
          'M15 8v1M15 15v1M11 12h1M18 12h1',
        ],
        circles: [{ cx: 15, cy: 12, r: 3 }],
      },
      sessions: {
        paths: [
          'M3 20c0-2.5 2-4.5 4.5-4.5h.5',
          'M21 20c0-2.5-2-4.5-4.5-4.5h-.5',
          'M10 10q2-3 4 0',
        ],
        circles: [
          { cx: 6, cy: 10, r: 2 },
          { cx: 18, cy: 10, r: 2 },
        ],
      },
      assessments: {
        paths: [
          'M16 17a4 4 0 0 1-4 4 6 6 0 0 1-6-6 7 7 0 0 1 4-6.3V6a3 3 0 0 1 6 0v1.7a7 7 0 0 1 3 5.3',
          'M9 13l2 2 4-4',
        ],
        circles: [],
      },
    }

    const navMenuGroupStateCookie = 'perform72-navMenuGroups'
    const defaultNavMenuGroups = {
      fitness: true,
      practice: true,
      flow: true,
      organization: true,
    }

    const getCookieValue = name => {
      if (typeof document === 'undefined') return null

      const cookie = document.cookie
        .split('; ')
        .find(item => item.startsWith(`${name}=`))

      return cookie ? cookie.substring(name.length + 1) : null
    }

    const readNavMenuGroups = () => {
      const value = getCookieValue(navMenuGroupStateCookie)
      if (!value) return { ...defaultNavMenuGroups }

      try {
        return { ...defaultNavMenuGroups, ...JSON.parse(decodeURIComponent(value)) }
      } catch {
        return { ...defaultNavMenuGroups }
      }
    }

    const writeNavMenuGroups = value => {
      if (typeof document === 'undefined') return

      document.cookie = [
        `${navMenuGroupStateCookie}=${encodeURIComponent(JSON.stringify(value))}`,
        'max-age=31536000',
        'path=/',
        'SameSite=Lax',
      ].join('; ')
    }

    const { hasRole } = useAuth()
    const { getUserAcademy } = useAcademies()
    const { contentScope, selectedAcademy, setContentScope } = useAcademySelection()
    const navMenuGroups = ref(readNavMenuGroups())
    const setNavMenuGroupState = (key, value) => {
      navMenuGroups.value = {
        ...navMenuGroups.value,
        [key]: value,
      }
      writeNavMenuGroups(navMenuGroups.value)
    }

    const userAcademyIds = ref([])
    const userAcademyNames = ref([])
    const isFullContentEnabled = ref(contentScope.value === 'full')

    watch(contentScope, scope => {
      isFullContentEnabled.value = scope === 'full'
    })

    const onContentScopeChange = value => {
      setContentScope(value ? 'full' : 'global')
      window.location.reload()
    }

    onMounted(async () => {
      if (hasRole('COACH')) {
        try {
          const user = await getUserAcademy()
          userAcademyIds.value = Array.isArray(user.companyId) ? user.companyId : []
          userAcademyNames.value = Array.isArray(user.academyName) ? user.academyName : []

          if (!Array.isArray(user.companyId) && user.companyId) {
            userAcademyIds.value = [user.companyId]
          }
          if (!Array.isArray(user.academyName) && user.academyName) {
            userAcademyNames.value = [user.academyName]
          }

          // Filter out empty or null IDs
          userAcademyIds.value = userAcademyIds.value.filter(id => id)
          userAcademyNames.value = userAcademyNames.value.filter(name => name)

          if ((!hasRole('ADMIN')) && (userAcademyIds.value.length === 1)) {
            const autoSelectedAcademy = {
              id: userAcademyIds.value[0],
              name: userAcademyNames.value[0] || 'My Academy',
            }
            localStorage.setItem('selectedAcademy', JSON.stringify(autoSelectedAcademy))
            axios.defaults.headers.common['X-Academy-Id'] = autoSelectedAcademy.id
          }
        } catch (error) {
          console.error('Error fetching academy for user:', error)

          // Reset arrays on error
          userAcademyIds.value = []
          userAcademyNames.value = []
        }
      }
    })

    return {
      hasRole,
      navMenuGroups,
      setNavMenuGroupState,
      theFlowIcons,
      isTheFlowEnabled,
      icons: {
        mdiAvTimer,
        mdiDumbbell,
        mdiGolf,
        mdiGolfTee,
        mdiHomeOutline,
        mdiWeightLifter,
        mdiFileChartOutline,
        mdiOfficeBuilding,
        mdiAccountGroup,
        mdiClipboardTextOutline,
        mdiFolderMultipleOutline,
        mdiChevronLeft,
        mdiChevronRight,
        mdiEarth,
        mdiDatabaseSearch,
      },
      userAcademyIds,
      userAcademyNames,
      selectedAcademy,
      isFullContentEnabled,
      onContentScopeChange,
    }
  },
}
</script>

<style lang="scss" scoped>
.app-title {
  font-size: 1.25rem;
  font-weight: 700;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.3px;
}

// ? Adjust this `translateX` value to keep logo in center when vertical nav menu is collapsed (Value depends on your logo)
.app-logo {
  transition: all 0.18s ease-in-out;
  .v-navigation-drawer--mini-variant & {
    display: none;
  }

  box-shadow: none;
  border-radius: 0;
}

@include theme(app-navigation-menu) using ($material) {
  background-color: map-deep-get($material, 'background');
}

.app-navigation-menu {
  padding-bottom: 112px;

  .nav-mini-toggle {
    flex-shrink: 0;
  }

  .vertical-nav-menu-items {
    padding-top: 0;
    padding-bottom: 0;
  }

  .mini-section-divider {
    width: 32px;
    height: 1px;
    margin: 5px auto;
    background-color: rgba(231, 227, 252, 0.16);
  }

  .nav-menu-section-group {
    margin-top: 0;
    padding-top: 0;
    border-top: 1px solid rgba(231, 227, 252, 0.16);

    ::v-deep .v-list-group__header {
      margin-top: 0;
    }

    ::v-deep .v-list-group__header > .v-list-item__content .v-list-item__title {
      font-size: 0.83rem !important;
      font-weight: 900 !important;
      text-transform: uppercase;
    }
  }

  .mini-rotated-logo {
    display: flex;
    justify-content: center;
    width: 80px;
    margin-top: 72px;
    padding-bottom: 0;
    transform: rotate(-90deg);
    transform-origin: center;

    .v-image {
      flex: 0 0 auto;
      box-shadow: none;
      border-radius: 0;
    }
  }

  &.v-navigation-drawer--mini-variant {
    .vertical-nav-header {
      justify-content: center;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .vertical-nav-menu-items {
      padding-right: 0 !important;
    }

    .v-list-item {
      justify-content: center;
      padding-left: 0 !important;
      padding-right: 0 !important;

      ::v-deep .v-list-item__icon {
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
    }
  }

  .v-list-item {
    &.vertical-nav-menu-link {
      ::v-deep .v-list-item__icon {
        .v-icon {
          transition: none !important;
        }
      }
    }
  }
}

.nav-build-info {
  position: absolute;
  right: 16px;
  bottom: 12px;
  left: 16px;
}

.content-scope-switcher + .nav-build-info {
  bottom: 96px;
}

.content-scope-switcher {
  position: absolute;
  right: 16px;
  bottom: 22px;
  left: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(94, 86, 105, 0.08);
}

.content-scope-title {
  margin-bottom: 8px;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  opacity: 0.72;
}

.content-scope-row {
  display: flex;
  align-items: center;
  min-height: 34px;
}

.content-scope-icon {
  flex: 0 0 auto;
  margin-right: 8px;
}

.content-scope-labels {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  line-height: 1.15;
}

.content-scope-main {
  overflow: hidden;
  font-size: 0.82rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-scope-sub {
  overflow: hidden;
  margin-top: 2px;
  font-size: 0.68rem;
  opacity: 0.7;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-scope-switch {
  flex: 0 0 auto;
  margin-top: 0;
  padding-top: 0;
}

</style>
